/**
 * Sub-Customers Redux Slice — the management list, paged and searched on the server.
 */

import { createSlice } from '@spcc/core'
import type { PayloadAction } from '@spcc/core'
import type { SubCustomer, SubCustomerFilter, SubCustomerType, PaginationMeta } from '../domain/types'
import { SUB_CUSTOMER_DEFAULT_PAGE_SIZE } from '../domain/constants'

export interface SubCustomerSaveRequest {
  name: string
  phone: string
  customerType: SubCustomerType
}

export interface SubCustomersState {
  items: SubCustomer[]
  filter: SubCustomerFilter
  pagination: PaginationMeta
  loading: boolean
  error: string | null
  form: { open: boolean; editing: SubCustomer | null; saving: boolean; error: string | null }
}

export const initialSubCustomersState: SubCustomersState = {
  items: [],
  filter: { search: '', customerType: null, page: 1, pageSize: SUB_CUSTOMER_DEFAULT_PAGE_SIZE, tenantDocumentId: null },
  pagination: { page: 1, pageSize: SUB_CUSTOMER_DEFAULT_PAGE_SIZE, pageCount: 1, total: 0 },
  loading: false,
  error: null,
  form: { open: false, editing: null, saving: false, error: null },
}

function startLoading(state: SubCustomersState) {
  state.loading = true
  state.error = null
}

export const subCustomersSlice = createSlice({
  name: 'subCustomers',
  initialState: initialSubCustomersState,
  reducers: {
    /** Page mount / header customer change (admin): back to page 1 for that scope. */
    init: (state, action: PayloadAction<{ tenantDocumentId?: string | null } | undefined>) => {
      state.filter = { ...state.filter, page: 1, tenantDocumentId: action.payload?.tenantDocumentId ?? null }
      startLoading(state)
    },
    fetchSubCustomers: startLoading,
    setSearch: (state, action: PayloadAction<string>) => {
      state.filter = { ...state.filter, search: action.payload, page: 1 }
      startLoading(state)
    },
    setCustomerType: (state, action: PayloadAction<SubCustomerType | null>) => {
      state.filter = { ...state.filter, customerType: action.payload, page: 1 }
      startLoading(state)
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.filter = { ...state.filter, page: action.payload }
      startLoading(state)
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.filter = { ...state.filter, pageSize: action.payload, page: 1 }
      startLoading(state)
    },
    fetchSuccess: (state, action: PayloadAction<{ items: SubCustomer[]; pagination: PaginationMeta }>) => {
      state.items = action.payload.items
      state.pagination = action.payload.pagination
      state.loading = false
    },
    fetchFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    openCreate: (state) => {
      state.form = { open: true, editing: null, saving: false, error: null }
    },
    openEdit: (state, action: PayloadAction<SubCustomer>) => {
      state.form = { open: true, editing: action.payload, saving: false, error: null }
    },
    closeForm: (state) => {
      if (!state.form.saving) state.form.open = false
    },
    saveSubCustomer: (state, _action: PayloadAction<SubCustomerSaveRequest>) => {
      state.form.saving = true
      state.form.error = null
    },
    saveSuccess: (state) => {
      state.form = { open: false, editing: null, saving: false, error: null }
    },
    saveFailure: (state, action: PayloadAction<string>) => {
      state.form.saving = false
      state.form.error = action.payload
    },
  },
})

export const subCustomersActions = subCustomersSlice.actions
export const subCustomersReducer = subCustomersSlice.reducer
